/**
 * useMultiAgentSelectors — typed hooks over the multiAgent selector layer.
 *
 * Components import THESE hooks, never raw `useMultiAgentStore(s => ...)`.
 * Each hook is a thin wrapper: selector → hook. Keeps components dumb,
 * makes selectors testable, and prevents duplicated inline derivations.
 */
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import {
  selectUserIntent,
  selectUserAttachments,
  selectHasActiveSession,
  selectIsTerminal,
  selectIsFreshEmptySession,
  selectAgentNodes,
  selectBusyNodes,
  selectRecentArtifacts,
  selectTotalTokens,
  selectStreamingText,
  selectIsStreaming,
} from "@/stores/multiAgentSelectors";

/** The first user message — the question for the current run. */
export const useUserIntent = () =>
  useMultiAgentStore(selectUserIntent);

/** Attachments on the sent user message — inline bubble thumbnails (#11). */
export const useUserAttachments = () =>
  useMultiAgentStore(selectUserAttachments);

/** True once a session has any content (run ID, prior turns, or non-idle status). */
export const useHasActiveSession = () =>
  useMultiAgentStore(selectHasActiveSession);

/** True when the run has reached done / failed / cancelled. */
export const useIsTerminal = () =>
  useMultiAgentStore(selectIsTerminal);

/** True when there's nothing to show — drives idle hero centering. */
export const useIsFreshEmptySession = () =>
  useMultiAgentStore(selectIsFreshEmptySession);

/** All agent nodes as a stable array. */
export const useAgentNodes = () =>
  useMultiAgentStore(selectAgentNodes);

/** Nodes currently running or calling a tool. */
export const useBusyNodes = () =>
  useMultiAgentStore(selectBusyNodes);

/** The N most recent artifacts, newest first (for the right-rail tile). */
export const useRecentArtifacts = (limit = 12) =>
  useMultiAgentStore(selectRecentArtifacts(limit));

/** Total token count (in + out) for the cost tile. */
export const useTotalTokens = () =>
  useMultiAgentStore(selectTotalTokens);

/** Merged streaming text across all nodes. */
export const useStreamingText = () =>
  useMultiAgentStore(selectStreamingText);

/** True while any node is still streaming. */
export const useIsStreaming = () =>
  useMultiAgentStore(selectIsStreaming);
